import { useState } from "react";
import type { FormEvent } from "react";
import { faqs } from "@/mocks/streams";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db4nk41uu2cnvnf27erg";

const contactItems = [
  {
    id: "email",
    icon: "ri-mail-send-line",
    label: "客服邮箱",
    value: "service@youyin-live.com",
    note: "工作日 9:00 - 21:00 回复",
  },
  {
    id: "partner",
    icon: "ri-hand-heart-line",
    label: "合作邮箱",
    value: "partner@youyin-live.com",
    note: "主播入驻与商务合作专用",
  },
  {
    id: "phone",
    icon: "ri-customer-service-2-line",
    label: "客服热线",
    value: "400-800-1234",
    note: "每日 9:00 - 24:00 在线",
  },
  {
    id: "address",
    icon: "ri-map-pin-line",
    label: "办公地址",
    value: "上海市浦东新区星光大道 88 号直播大厦 12 层",
    note: "到访请提前预约",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [openFaq, setOpenFaq] = useState<string | null>(faqs[0]?.id ?? null);

  usePageMeta(
    "联系我们 - 有因直播",
    "通过邮箱、客服热线或在线反馈表单联系有因直播团队，主播入驻、商务合作与问题反馈均可在此提交。",
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => params.append(key, String(value)));

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试或通过邮箱联系我们。");
      }
    } catch (err) {
      setStatus("error");
      setFormError(
        err instanceof Error ? err.message : "网络异常，提交失败，请稍后重试或通过客服邮箱联系我们。",
      );
    }
  };

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-10">
      <section className="rounded-xl border border-background-200/70 bg-background-100 p-6 md:p-10">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/12 px-3 py-1 text-xs text-primary-500">
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-customer-service-line"></i>
          </span>
          联系我们
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">随时与我们取得联系</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-500">
          无论你是想入驻成为主播、洽谈商务合作，还是希望反馈使用中遇到的问题，都可以通过下面的方式联系有因直播团队，
          我们会认真对待每一条消息。
        </p>
      </section>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">联系方式</h2>
          <div className="mt-4 space-y-3">
            {contactItems.map((item) => (
              <div key={item.id} className="flex items-start gap-4 rounded-lg border border-background-200/70 bg-background-100 p-4">
                <span className="w-11 h-11 flex flex-shrink-0 items-center justify-center rounded-md bg-primary-500/12 text-primary-500">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-400">{item.label}</p>
                  <p className="mt-1 break-words text-sm font-medium text-foreground-900">{item.value}</p>
                  <p className="mt-1 text-xs text-foreground-400">{item.note}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 rounded-lg border border-background-200/70 bg-background-100 p-4">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500/12 text-accent-400">
                <i className="ri-time-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">服务时间</p>
                <p className="mt-0.5 text-xs text-foreground-400">客服热线每日 9:00 - 24:00，邮箱 7×24 小时接收</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-xl border border-background-200/70 bg-background-100 p-5 md:p-7">
            <h2 className="font-heading text-lg font-semibold text-foreground-950">在线反馈表单</h2>
            <p className="mt-2 text-sm text-foreground-400">填写下方信息，我们会在三个工作日内通过邮箱回复你。</p>

            <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm text-foreground-600">你的称呼</span>
                  <input
                    name="name"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="请输入你的称呼"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder-foreground-300 outline-none focus:border-primary-400"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm text-foreground-600">联系邮箱</span>
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder-foreground-300 outline-none focus:border-primary-400"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm text-foreground-600">咨询类型</span>
                <select
                  name="topic"
                  defaultValue="主播入驻"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
                >
                  <option value="主播入驻">主播入驻</option>
                  <option value="商务合作">商务合作</option>
                  <option value="内容举报">内容举报</option>
                  <option value="产品建议">产品建议</option>
                  <option value="其他问题">其他问题</option>
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm text-foreground-600">留言内容</span>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请简要描述你的问题或合作意向（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder-foreground-300 outline-none focus:border-primary-400"
                />
              </label>

              <input
                className="alt-contact-field"
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "error" && (
                <p className="flex items-start gap-2 rounded-md bg-primary-500/10 px-3 py-2.5 text-sm text-primary-500">
                  <span className="mt-0.5 w-4 h-4 flex flex-shrink-0 items-center justify-center">
                    <i className="ri-error-warning-line"></i>
                  </span>
                  {formError || "提交失败，请稍后重试。"}
                </p>
              )}

              {status === "success" && (
                <p className="flex items-start gap-2 rounded-md bg-accent-500/12 px-3 py-2.5 text-sm text-accent-400">
                  <span className="mt-0.5 w-4 h-4 flex flex-shrink-0 items-center justify-center">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  提交成功，感谢你的反馈！我们会尽快通过邮箱与你联系。
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 sm:w-auto sm:px-8"
              >
                {status === "submitting" ? (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin"></i>
                    </span>
                    提交中…
                  </>
                ) : (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-send-plane-line"></i>
                    </span>
                    提交反馈
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold text-foreground-950">常见问题</h2>
        <p className="mt-1 text-sm text-foreground-400">这里整理了一些观众与主播最常问到的问题</p>
        <div className="mt-4 space-y-3">
          {faqs.map((faq) => {
            const open = openFaq === faq.id;
            return (
              <div key={faq.id} className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? null : faq.id)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="flex items-center gap-3 text-sm font-medium text-foreground-900">
                    <span className="w-5 h-5 flex flex-shrink-0 items-center justify-center text-primary-500">
                      <i className="ri-question-line"></i>
                    </span>
                    {faq.question}
                  </span>
                  <span className="w-5 h-5 flex flex-shrink-0 items-center justify-center text-foreground-400">
                    <i className={open ? "ri-subtract-line" : "ri-add-line"}></i>
                  </span>
                </button>
                {open && (
                  <p className="border-t border-background-200/70 px-5 py-4 pl-12 text-sm leading-relaxed text-foreground-500">
                    {faq.answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}